import styled from "@emotion/styled";
import { useState, useRef, KeyboardEvent, SyntheticEvent, Fragment, ReactNode } from "react";
import useAutocomplete, { AutocompleteHighlightChangeReason } from "@mui/material/useAutocomplete";
import { KalturaESearchHistory } from "kaltura-typescript-client/api/types/KalturaESearchHistory";
import { ClickAwayListener } from "@kaltura/mediaspace-shared-ui";
import { FilterMenu, FilterMenuProps } from "./filter-menu/FilterMenu";
import KeywordSearch, { KeywordSearchProps } from "./keyword-search/KeywordSearch";
import useSearchSuggestions from "./search-suggestions/useSearchSuggestions";
import { SuggestionsList, SuggestionsListHandle } from "./search-suggestions/suggestions-list/SuggestionsList";

export interface SearchBarProps {
    /**
     * props for the filter menu component
     */
    filterMenuProps?: FilterMenuProps;

    /**
     * props for the keyword-search component
     */
    keywordSearchProps: KeywordSearchProps;

    /**
     * buttons to render on the right end of the bar
     */
    buttons?: ReactNode[];
    /**
     * buttons to render on the left end of the bar
     */
    leftButtons?: ReactNode[];

    /**
     * should search input take full screen width
     */
    fullWidth?: boolean;
}

const StyledSearchBar = styled(
    "div",
    {
        shouldForwardProp(propName) {
            return propName !== "fullWidth";
        },
    }
)<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: fullWidth ? "100%" : "auto",
    gap: theme.spacing(1),
}));

const StyledKewordSearchWrapper = styled(
    "div",
    {
        shouldForwardProp(propName) {
            return propName !== "fullWidth";
        },
    }
)<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
    width: fullWidth ? "100%" : "auto",
}));

const StyledMenuWrapper = styled("div")<{ hasButtons: boolean }>(({ hasButtons, theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    flexGrow: 1,
    [theme.breakpoints.down("sm")]: {
        flexGrow: hasButtons ? 1 : 0,
    },
}));

const StyledButtonsWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
}));

/**
 * Search bar with keyword search, search settings and filters modal button
 */
export function SearchBar(props: SearchBarProps) {
    const { filterMenuProps, keywordSearchProps, buttons, leftButtons, fullWidth } = props;
    const [keywordSuggestion, setKeywordSuggestion] = useState(keywordSearchProps.value || "");
    const [open, setOpen] = useState<boolean>(true);
    const highLightedOptionRef = useRef<KalturaESearchHistory | null>(null);
    const suggestionListRef = useRef<SuggestionsListHandle>(null);
    const keywordSearchRef = useRef<HTMLInputElement>(null);

    // user input change
    const handleSubmit = (value: string) => {
        // update form value
        keywordSearchProps.onSubmit(value);
        // update suggestions seed
        setKeywordSuggestion(value);
    };

    const handleKeywordChange = (value: string) => {
        // update suggestions seed
        setKeywordSuggestion(value);
    };

    // suggestion selected
    const handleSuggestionSubmit = (value: string) => {
        // update form value
        keywordSearchProps.onSubmit(value);
        // update suggestions seed
        setKeywordSuggestion(value);
    };

    // show menu iff there is a keyword and menu data:
    const menu = keywordSearchProps.value && filterMenuProps ? <FilterMenu {...filterMenuProps} /> : undefined;

    // search suggestion highLight
    const handleHighlightChange = (
        event: SyntheticEvent,
        option: KalturaESearchHistory | null,
        reason: AutocompleteHighlightChangeReason
    ) => {
        if (reason === "keyboard") {
            // keep track of active suggestion
            highLightedOptionRef.current = option;
            // reset focus on the input
            keywordSearchRef.current?.focus();
        }
    };

    // get search suggestions
    const { suggestions, removeTerm } = useSearchSuggestions(keywordSuggestion || "");

    // mui autocomplete functionality
    const { getRootProps, getInputProps, getListboxProps, getOptionProps, groupedOptions } = useAutocomplete({
        clearOnEscape: true,
        options: suggestions || [],
        value: { searchTerm: keywordSuggestion } as KalturaESearchHistory,
        freeSolo: true,
        autoHighlight: true,
        autoComplete: true,
        getOptionLabel: (option) => {
            return (option as KalturaESearchHistory).searchTerm;
        },
        isOptionEqualToValue: (option, value) => option.searchTerm === value.searchTerm,
        filterOptions: (x) => x,
        onHighlightChange: handleHighlightChange,
    });

    const rootProps = getRootProps();
    const inputProps = getInputProps();
    const listboxProps = getListboxProps();

    // set focus for the `remove` suggestion button
    const handleArrowRight = (event: KeyboardEvent<HTMLInputElement>) => {
        if (groupedOptions.length > 0 && event.key === "ArrowRight") {
            // set focus on the `remove` button of the highlighted search suggestion
            suggestionListRef.current?.setOptionRemoveFocus(highLightedOptionRef.current?.searchTerm || "");
        }
    };

    // reset focus on the input from the `remove` button
    const handleArrowLeft = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowLeft") {
            // reset focus on the input
            keywordSearchRef.current?.focus();
        }
    };

    // remove search suggestion
    const handleRemoveTerm = (searchTerm: string) => {
        // remove the suggestion from the api
        removeTerm(searchTerm);
        // reset focus on the input
        keywordSearchRef.current?.focus();
    };

    // force close the autocomplete suggestions if there was a click outside it
    const handleClose = () => {
        setOpen(false);
    };

    // allow to open the autocomplete suggestions after input focus
    const handleOpen = () => {
        setOpen(true);
    };

    return (
        <StyledSearchBar fullWidth={fullWidth}>
            <ClickAwayListener onClickAway={handleClose}>
                <StyledKewordSearchWrapper fullWidth={fullWidth} {...rootProps}>
                    <KeywordSearch
                        {...keywordSearchProps}
                        endAdornment={menu}
                        onSubmit={handleSubmit}
                        onChange={handleKeywordChange}
                        onKeyDown={handleArrowRight}
                        onFocus={handleOpen}
                        inputProps={inputProps}
                        inputRef={keywordSearchRef}
                        fullWidth={fullWidth}
                    />
                    {open && groupedOptions.length > 0 && (
                        <SuggestionsList
                            ref={suggestionListRef}
                            listboxProps={listboxProps}
                            getOptionProps={getOptionProps}
                            suggestions={groupedOptions as KalturaESearchHistory[]}
                            onItemSelect={handleSuggestionSubmit}
                            onItemRemove={handleRemoveTerm}
                            onKeyDown={handleArrowLeft}
                        />
                    )}
                </StyledKewordSearchWrapper>
            </ClickAwayListener>
            <StyledMenuWrapper hasButtons={!!buttons}>
                <StyledButtonsWrapper className="kms-ds-left-searchbar-buttons">
                    {leftButtons?.map((btn, index) => <Fragment key={`left_${index}`}>{btn}</Fragment>)}
                </StyledButtonsWrapper>
                {buttons && (
                    <StyledButtonsWrapper className="kms-ds-open-filters-menu">
                        {buttons?.map((btn, index) => <Fragment key={`right_${index}`}>{btn}</Fragment>)}
                    </StyledButtonsWrapper>
                )}
            </StyledMenuWrapper>
        </StyledSearchBar>
    );
}

export default SearchBar;
